import {SelectAsync} from '@wandb/weave/components/Form/Select';
import {debounce} from 'lodash';
import React, {useCallback} from 'react';

import {DD_PRIVACY_ALLOW} from '../../../../util/datadog/privacy';
import {type SettingSelectOptionType} from '../types';

const MAX_OPTIONS_DISPLAYED = 100;

export type LinePlotXAxisProps<OptionType extends SettingSelectOptionType> = {
  id?: string;
  className?: string;
  options: OptionType[];
  value: OptionType | undefined;
  onChange: (selected: OptionType | null) => void;
  cacheOptions?: boolean;
  /**
   * Stable Datadog RUM action name for this select. Names both the control
   * (so opening the dropdown is groupable) and its options (`<ddActionName>
   * option`). Required so each usage of this shared select stays
   * distinguishable in RUM instead of collapsing into one generic name.
   */
  ddActionName: string;
  disabled?: boolean;
};

/**
 * This is a Select component used in the settings drawer.
 * It is searchable and will truncate options to a max list of 100
 * to provide better UX experience for workspaces with a large
 * set of metrics.
 */
export const SettingSelect = <OptionType extends SettingSelectOptionType>({
  id,
  className,
  onChange,
  options,
  value,
  cacheOptions = true,
  ddActionName,
  disabled = false,
}: LinePlotXAxisProps<OptionType>) => {
  const loadOptions = useCallback(
    (inputValue: string, callback: (options: OptionType[]) => void) => {
      const searchText = inputValue.trim();
      if (searchText === '') {
        callback(truncateOptions(options));
        return;
      }

      const filteredOptions = options.filter(option =>
        option.label.toLowerCase().includes(searchText.toLowerCase())
      );
      callback(truncateOptions(filteredOptions));
    },
    [options]
  );

  const debouncedLoadOptions = debounce(loadOptions, 300);

  // `display: contents` keeps the wrapper out of layout while still letting Datadog
  // read the action name from the clicked control. Also unmasks the control and its
  // options in replay — workspace config, not credentials/account data.
  return (
    <div
      style={{display: 'contents'}}
      data-dd-action-name={ddActionName}
      data-dd-privacy={DD_PRIVACY_ALLOW}>
      <SelectAsync<OptionType>
        id={id}
        cacheOptions={cacheOptions}
        className={className}
        defaultOptions
        isDisabled={disabled}
        formatOptionLabel={({label, value: selectedValue}) => (
          <div
            role="option"
            aria-selected={value?.value === selectedValue}
            data-dd-action-name={`${ddActionName} option`}>
            <span>{label}</span>
          </div>
        )}
        isSearchable
        loadOptions={debouncedLoadOptions}
        onChange={onChange}
        options={options}
        value={value}
      />
    </div>
  );
};

const truncateOptions = <OptionType extends SettingSelectOptionType>(
  options: OptionType[]
) => {
  return options.slice(0, MAX_OPTIONS_DISPLAYED);
};
